import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Skeleton, TextField } from '@mui/material';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { getJugadoresByLetrasJugador } from '../../services/jugadorEstadisticasService';
import { playerName } from '../../utils/playerRanking';
import DirectoryPagination from './DirectoryPagination';
import PlayerRoleBadge from '../playerStatistics/PlayerRoleBadge';
import { isGoalkeeper } from '../../utils/playerDiscovery';

export default function PlayerGlobalSearch({ query, onChange, page: requestedPage = 1, onPageChange }) {
  const { t, i18n } = useTranslation('directories');
  const location = useLocation();
  const theme = useTheme();
  const text = query.trim();
  const [debouncedText, setDebouncedText] = useState(text);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedText(text), 500);
    return () => clearTimeout(timer);
  }, [text]);
  const ready = text.length >= 3;
  const waiting = ready && text !== debouncedText;
  const search = useQuery({
    queryKey: ['jugadorEstadisticas', 'globalNames', debouncedText],
    queryFn: async ({ signal }) => {
      const rows = await getJugadoresByLetrasJugador(debouncedText, null, { signal });
      if (!Array.isArray(rows) || rows.some(row => row?.idJugador == null || !playerName(row).trim())) {
        throw new Error('Invalid player search response');
      }
      return rows;
    },
    enabled: ready && !waiting,
  });
  const players = useMemo(() => {
    const unique = new Map();
    (search.data || []).forEach(player => {
      const id = String(player.idJugador);
      if (!unique.has(id)) unique.set(id, player);
    });
    return [...unique.values()].sort((first, second) =>
      playerName(first).localeCompare(playerName(second), i18n.language));
  }, [search.data, i18n.language]);
  const pageSize = 25;
  const page = Math.min(Math.max(1, Math.floor(Number(requestedPage)) || 1),
    Math.max(1, Math.ceil(players.length / pageSize)));
  const loading = ready && (waiting || search.isLoading);
  const profileState = { directoryFrom: location.pathname + location.search };
  return <section className="dr-panel dr-global-search" aria-label={t('globalPlayers.title')} aria-busy={loading}>
    <TextField fullWidth label={t('searchPlayer')} placeholder={t('globalPlayers.placeholder')}
      helperText={t('globalPlayers.minimum')} value={query} onChange={event => onChange(event.target.value)} />
    {!ready && <p className="dr-empty" role="status">{t('globalPlayers.prompt')}</p>}
    {loading && <div role="status" aria-label={t('loading')}>
      {[1, 2, 3].map(key => <Skeleton key={key} height={64} />)}
    </div>}
    {ready && !loading && search.isError && <Alert severity="error"
      action={<Button onClick={() => search.refetch()}>{t('comparison.retry')}</Button>}>
      {t('globalPlayers.error')}
    </Alert>}
    {ready && !loading && search.isSuccess && <>
      <div className="dr-panel-header"><div><h2>{t('globalPlayers.resultsTitle')}</h2>
        <p role="status">{t('globalPlayers.results', { count: players.length })}</p></div></div>
      {!players.length && <p className="dr-empty">{t('globalPlayers.empty')}</p>}
      <ul className="dr-global-player-list">
        {players.slice((page - 1) * pageSize, page * pageSize).map(player => {
          const url = `/jugador?${new URLSearchParams({ idJugador: player.idJugador })}`;
          return <li key={String(player.idJugador)} data-theme={theme.palette.mode}
            className={`dr-global-player-row${isGoalkeeper(player) ? ' player-goalkeeper' : ''}`}>
            <div><Link className="dr-name" to={url} state={profileState}>{playerName(player)}</Link>
              <PlayerRoleBadge player={player} />
              <small className="dr-muted">{t('globalPlayers.playerId', { id: player.idJugador })}</small></div>
            <Button component={Link} to={url} state={profileState} endIcon={<ArrowForward />}
              aria-label={t('globalPlayers.openNamed', { name: playerName(player) })}>
              {t('discovery.profileShort')}
            </Button>
          </li>;
        })}
      </ul>
      <DirectoryPagination page={page} pageSize={pageSize} total={players.length} onChange={onPageChange} />
    </>}
  </section>;
}
